import { GridSortModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ContextClientInsurerCollectionType } from '../../../../context/billing.context';
import { useClientInsurersByIdsLazy } from '../../../../hooks/client-insurer-hook';
import { useAbortableTrigger } from '../../../../hooks/use-abortable-trigger';
import {
  BillingType,
  BillsPaginatedType,
  BillStatusEnum,
  ClientType,
  ContractsPaginatedType,
  InsurerType,
  PartnershipType,
  SubmissionStatusEnum,
  useGetAllClientsQuery,
  useGetAllInsurersQuery,
  useGetBillingTypesQuery,
  useGetBillsLazyQuery,
  useGetContractIdsLazyQuery,
  useGetContractsV2LazyQuery,
  useGetPartnershipsQuery,
} from '../../../../types';
import { selectSorterFunction } from '../../../../utils/bills-utils';
import { BillManagementGridFilterModelType } from '../../bill-grid-components/bill-management-types';

export interface BillQueriesType {
  ciData: ContextClientInsurerCollectionType;
  contractsData: ContractsPaginatedType;
  clientsData: ClientType[];
  insurersData: Omit<InsurerType, 'eligibilityRef' | 'memberIdRequired'>[];
  partnershipsData: PartnershipType[];
  billingTypesData: BillingType[];
  billsData: BillsPaginatedType;
  isLoading: boolean;
  hasError: boolean;
  searchClientInsurerIds: (ids: Set<number>) => void;
}

const sortByObj = {
  dateOfService: 'bill_date_of_service',
  submission: 'submission_submission_date',
};

const emptyBillsData: BillsPaginatedType = {
  items: [],
  page: 1,
  pages: 1,
  size: 0,
};

export const useBillQueries = (
  filterParams: BillManagementGridFilterModelType,
  paginationModel: { page: number; pageSize: number },
  sortModel: GridSortModel,
  enabled: boolean,
): BillQueriesType => {
  const { data: ciData, searchClientInsurerIds } = useClientInsurersByIdsLazy();

  const [
    getContracts,
    { data: contractsData, loading: contractsLoading, error: contractsError },
  ] = useGetContractsV2LazyQuery();

  const [getContractIds] = useGetContractIdsLazyQuery();

  const {
    data: clientsData,
    error: clientsError,
    loading: clientsLoading,
  } = useGetAllClientsQuery();

  const {
    data: insurersData,
    error: insurersError,
    loading: insurersLoading,
  } = useGetAllInsurersQuery();

  const { data: partnershipsData, loading: partnershipsLoading } =
    useGetPartnershipsQuery({
      variables: { type: 'billing' },
    });

  const [getBills, { loading: billsLoading }] = useGetBillsLazyQuery();

  const {
    data: billingTypesData,
    loading: billingTypesLoading,
    error: billingTypesError,
  } = useGetBillingTypesQuery();

  const [billsResult, setBillsResult] =
    useState<BillsPaginatedType>(emptyBillsData);
  const [fetchError, setFetchError] = useState(false);

  const stableFilterParams = useMemo(
    () => ({
      clientIds: filterParams.clientIds,
      insurerIds: filterParams.insurerIds,
      partnershipIds: filterParams.partnershipIds,
      userIds: filterParams.userIds,
      source: filterParams.source,
      sourceIds: filterParams.sourceIds,
      endDate: filterParams.endDate,
      startDate: filterParams.startDate,
      held: filterParams.held,
      submitted: filterParams.submitted,
      holdReasons: filterParams.holdReasons,
      billingTypes: filterParams.billingTypes,
      submissionStatusTypes: filterParams.submissionStatusTypes,
      paymentTypes: filterParams.paymentTypes,
      billIds: filterParams.billIds,
      sourceStartDate: filterParams.sourceStartDate,
      sourceEndDate: filterParams.sourceEndDate,
    }),
    [
      filterParams.clientIds,
      filterParams.insurerIds,
      filterParams.partnershipIds,
      filterParams.userIds,
      filterParams.source,
      filterParams.sourceIds,
      filterParams.endDate,
      filterParams.startDate,
      filterParams.held,
      filterParams.submitted,
      filterParams.holdReasons,
      filterParams.billingTypes,
      filterParams.submissionStatusTypes,
      filterParams.paymentTypes,
      filterParams.billIds,
      filterParams.sourceStartDate,
      filterParams.sourceEndDate,
    ],
  );

  const stablePaginationModel = useMemo(
    () => ({
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    }),
    [paginationModel.page, paginationModel.pageSize],
  );

  const fetchBills = useCallback(
    async (signal: AbortSignal): Promise<void> => {
      let contractIds: number[] = [];

      try {
        if (
          stableFilterParams.clientIds.length > 0 ||
          stableFilterParams.insurerIds.length > 0 ||
          stableFilterParams.billingTypes.length > 0 ||
          stableFilterParams.partnershipIds.length > 0
        ) {
          const { data, error } = await getContractIds({
            variables: {
              clientIds: stableFilterParams.clientIds,
              insurerIds: stableFilterParams.insurerIds,
              billingTypes: stableFilterParams.billingTypes.map(type =>
                type.toLowerCase(),
              ),
              partnershipIds: stableFilterParams.partnershipIds,
              // Include voided contracts so filtering by client/insurer/partnership
              // still surfaces bills on voided contracts (record-level void is
              // filtered separately and stays excluded).
              includeVoided: true,
            },
            context: { fetchOptions: { signal } },
          });
          if (signal.aborted) return;
          if (error) {
            setFetchError(true);
            setBillsResult(emptyBillsData);
            return;
          }
          if (data?.getContractIds?.length) {
            contractIds = data.getContractIds;
          } else {
            setFetchError(false);
            setBillsResult(emptyBillsData);
            return;
          }
        }
        const result = await getBills({
          variables: {
            page: stablePaginationModel.page + 1,
            size: stablePaginationModel.pageSize,
            userIds: stableFilterParams.userIds,
            source: stableFilterParams.source,
            sourceIds: stableFilterParams.sourceIds,
            contractIds,
            endDate: stableFilterParams.endDate,
            startDate: stableFilterParams.startDate,
            billStatuses: [
              BillStatusEnum.Submittable,
              BillStatusEnum.SubmittableWithBypass,
            ],
            held: stableFilterParams.held,
            submitted: stableFilterParams.submitted,
            internalHoldTypes: stableFilterParams.holdReasons,
            submissionStatuses:
              stableFilterParams.submissionStatusTypes as SubmissionStatusEnum[],
            sortBy: sortModel.map(
              item =>
                [
                  sortByObj[item.field as keyof typeof sortByObj],
                  item.sort,
                ].filter(Boolean) as string[],
            ),
            paymentTypes: stableFilterParams.paymentTypes,
            billIds: stableFilterParams.billIds,
            sourceStartDate: stableFilterParams.sourceStartDate,
            sourceEndDate: stableFilterParams.sourceEndDate,
          },
          context: { fetchOptions: { signal } },
        });
        if (signal.aborted) return;
        if (result.error) {
          setFetchError(true);
          setBillsResult(emptyBillsData);
          return;
        }
        setFetchError(false);
        setBillsResult(result.data?.getBills ?? emptyBillsData);
      } catch {
        if (!signal.aborted) {
          setFetchError(true);
          setBillsResult(emptyBillsData);
        }
      }
    },
    [
      stableFilterParams,
      stablePaginationModel,
      getContractIds,
      getBills,
      sortModel,
    ],
  );

  const refetch = useAbortableTrigger(fetchBills);

  useEffect(() => {
    if (!enabled) {
      setBillsResult(emptyBillsData);
      return;
    }
    refetch();
  }, [enabled, refetch]);

  // Fetch contract data to enrich bill rows.
  // Only fetch contracts for the currently returned bills for performance.
  useEffect(() => {
    const contractIds = billsResult.items
      .map(bill => bill.contractId)
      .filter((id): id is number => id != null);
    if (contractIds.length > 0) {
      getContracts({
        variables: {
          size: stablePaginationModel.pageSize,
          page: 1,
          contractIds,
          requireBillingTypeOverrideMatch: false,
          // Include voided contracts so bills on a voided contract still resolve
          // their client/insurer for display instead of showing "Loading..." forever.
          includeVoided: true,
        },
      });
    }
  }, [billsResult, stablePaginationModel.pageSize, getContracts]);

  const hasError =
    fetchError ||
    !!contractsError ||
    !!clientsError ||
    !!insurersError ||
    !!billingTypesError;

  const initialDataLoading =
    clientsLoading ||
    insurersLoading ||
    partnershipsLoading ||
    billingTypesLoading;

  const isLoading = billsLoading || contractsLoading || initialDataLoading;

  return useMemo(
    () => ({
      ciData,
      contractsData: contractsData?.getContractsV2 ?? {
        items: [],
        page: 1,
        pages: 1,
        size: 0,
      },
      clientsData: [...(clientsData?.getAllClients ?? [])].sort((a, b) =>
        selectSorterFunction(a.name, b.name),
      ),
      insurersData: [...(insurersData?.getAllInsurers ?? [])].sort((a, b) =>
        selectSorterFunction(a.name, b.name),
      ),
      partnershipsData: [...(partnershipsData?.getPartnerships ?? [])].sort(
        (a, b) => selectSorterFunction(a.name, b.name),
      ),
      billsData: billsResult,
      billingTypesData: [...(billingTypesData?.getBillingTypes ?? [])].sort(
        (a, b) => selectSorterFunction(a.value, b.value),
      ),
      isLoading,
      hasError,
      searchClientInsurerIds,
    }),
    [
      ciData,
      contractsData?.getContractsV2,
      clientsData?.getAllClients,
      insurersData?.getAllInsurers,
      partnershipsData?.getPartnerships,
      billsResult,
      billingTypesData?.getBillingTypes,
      isLoading,
      hasError,
      searchClientInsurerIds,
    ],
  );
};
